import React, { useState, useEffect } from 'react';
import { TextField, Typography, Grid, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { safeParseISO } from '../../utils/financialCalculations.js';
import { genderOptions, educationLevels, countryOptions, employmentStatusOptions, usStates, canadianProvinces, claimantSectorOptions,  } from '../OptionDefinitions.js';

const ClaimantInformationStep = ({ formik }) => {
  const { values, touched, errors, handleChange, handleBlur, setFieldValue } = formik;

  // Local state for date fields
  const [dob, setDob] = useState(values.dateOfBirth ? safeParseISO(values.dateOfBirth) : null);
  const [incidentDate, setIncidentDate] = useState(values.incidentDate ? safeParseISO(values.incidentDate) : null);

  useEffect(() => {
    setDob(values.dateOfBirth ? safeParseISO(values.dateOfBirth) : null);
  }, [values.dateOfBirth]);

  useEffect(() => {
    setIncidentDate(values.incidentDate ? safeParseISO(values.incidentDate) : null);
  }, [values.incidentDate]);

  const handleDobChange = (date) => {
    setDob(date);
    setFieldValue('dateOfBirth', date, true); // Set value and mark as validating
  };

  const handleIncidentDateChange = (date) => {
    setIncidentDate(date);
    setFieldValue('incidentDate', date, true); // Set value and mark as validating
  };

  const getRegionOptions = () => {
    if (values.country === 'US') return usStates;
    if (values.country === 'Canada') return canadianProvinces;
    return [];
  };

  const handleCountryChange = (event) => {
    const { value } = event.target;
    setFieldValue('country', value);
    setFieldValue('provinceOrState', ''); // Reset state/province when country changes
    if (value !== 'Canada') {
      setFieldValue('claimantSector', ''); // Reset sector if not Canada
    }
  };

  const regionLabel = values.country === 'Canada' ? "Province *" : values.country === 'US' ? "State *" : "Province/State *";
  const showClaimantSectorField = values.country === 'Canada' &&
                                  ['Employed Full-Time', 'Employed Part-Time'].includes(values.employmentStatus);

  return (
    <>
      <Typography variant="h6" gutterBottom>Claimant Information</Typography>
      <Grid container spacing={3}>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <TextField
            name="fullName"
            label="Full Name *"
            fullWidth
            value={values.fullName ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.fullName && Boolean(errors.fullName)}
            helperText={touched.fullName && errors.fullName ? errors.fullName : "Full legal name of the person being evaluated."}
          />
        </Grid>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <DatePicker
            label="Date of Birth *"
            value={dob ?? ''}
            onChange={handleDobChange}
            onClose={() => formik.setFieldTouched('dateOfBirth')}
            maxDate={new Date()}
            format="yyyy-MM-dd"
            slotProps={{
              textField: {
                name: 'dateOfBirth',
                fullWidth: true,
                error: touched.dateOfBirth && Boolean(errors.dateOfBirth),
                helperText: touched.dateOfBirth && errors.dateOfBirth ? errors.dateOfBirth : "Date of Birth for claimant (YYYY-MM-DD).",
                onBlur: handleBlur,
              },
            }}
          />
        </Grid>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <DatePicker
            label="Incident Date *"
            value={incidentDate ?? ''}
            onChange={handleIncidentDateChange}
            onClose={() => formik.setFieldTouched('incidentDate')}
            maxDate={new Date()}
            format="yyyy-MM-dd"
            slotProps={{
              textField: {
                name: 'incidentDate',
                fullWidth: true,
                error: touched.incidentDate && Boolean(errors.incidentDate),
                helperText: touched.incidentDate && errors.incidentDate ? errors.incidentDate : "Date of the incident or loss (YYYY-MM-DD).",
                onBlur: handleBlur,
              },
            }}
          />
        </Grid>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <TextField
            name="gender"
            label="Gender"
            select
            fullWidth
            value={values.gender ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            helperText="Gender identity of the claimant (e.g., Male, Female)."
          >
            {genderOptions.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <TextField
            name="educationLevel"
            label="Highest Level of Education Completed"
            select
            fullWidth
            value={values.educationLevel ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            helperText="Highest level of education completed (e.g., High School, Bachelor's Degree)."
            slotProps={{
              formHelperText: {
                sx: {maxWidth: 520}
              }
            }}
          >
            {educationLevels.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <TextField
            name="employmentStatus"
            label="Employment Status (at time of incident)"
            select
            fullWidth
            value={values.employmentStatus ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            helperText="Employment status at time of incident (e.g., Employed Full-Time, Part-Time, Unemployed)."
            slotProps={{
              formHelperText: {
                sx: {maxWidth: 520}
              }
            }}
          >
            {employmentStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>
        {showClaimantSectorField && (
          <Grid size={{xs:12, md:6, lg: 4}}>
            <TextField
              name="claimantSector"
              label="Employment Sector (Canada)"
              select
              fullWidth
              value={values.claimantSector ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText="Claimant's employment sector in Canada (Public/Private)."
            slotProps={{
              formHelperText: {
                sx: {maxWidth: 520}
              }
            }}
            >
              <MenuItem value=""><em>Select Sector</em></MenuItem>
              {claimantSectorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid size={{xs:12, md:6, lg: 4}}>
          <TextField
            name="country"
            label="Country *"
            select
            fullWidth
            value={values.country ?? ''}
            onChange={handleCountryChange}
            onBlur={handleBlur}
            error={touched.country && Boolean(errors.country)}
            helperText={touched.country && errors.country ? errors.country : "The country where the claimant resides (for life expectancy tables)."}
            slotProps={{
              formHelperText: {
                sx: {maxWidth: 520}
              }
            }}
          >
            <MenuItem value=""><em>Select Country</em></MenuItem>
            {countryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{xs:12, md:6, lg: 4}}>
          <TextField
            name="provinceOrState"
            label={regionLabel}
            select
            fullWidth
            value={values.provinceOrState ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.provinceOrState && Boolean(errors.provinceOrState)}
            helperText={touched.provinceOrState && errors.provinceOrState ? errors.provinceOrState : (values.country === "Canada" ? "The Canadian province where the claimant resides." : values.country === "US" ? "The U.S. state where the claimant resides, used for wage adjustments." : "The province or state where the claimant resides.")}
            disabled={!values.country}
          >
            <MenuItem value=""><em>Select {values.country === "Canada" ? "Province" : "State"}</em></MenuItem>
            {getRegionOptions().map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </>
  );
};

export default ClaimantInformationStep;